import { IUser } from '@/models/user.model'
import { webApiService } from '@/services'
import { Button, Chip, Progress, Spinner, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@nextui-org/react'
import { useCallback, useEffect, useState } from 'react'
import { Accept, useDropzone } from 'react-dropzone'
import { MdCheckCircle, MdClose, MdError, MdFileDownload, MdInsertDriveFile, MdUploadFile } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import XLSX from 'xlsx-js-style'

interface IExpectedColumn {
  name: string
  required: boolean
}

interface IRow {
  brand: string
  model: string
  capacity: string | null
}

interface IResult {
  ok: boolean
  message: string
}

/** Columnas del excel, en el orden de la plantilla. La capacidad es opcional. */
const EXPECTED_COLUMNS: IExpectedColumn[] = [
  { name: 'Marca', required: true },
  { name: 'Modelo', required: true },
  { name: 'Capacidad', required: false }
]

const ACCEPT: Accept = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
}

/** Normaliza un encabezado para compararlo sin tildes ni mayúsculas. */
const normalize = (value: string): string =>
  String(value).trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

const BulkTelcelPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const userState = useSelector((state: { user: IUser }) => state.user)

  const [store, setStore] = useState<any>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<IRow[]>([])
  const [skipped, setSkipped] = useState(0)
  const [parsing, setParsing] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [results, setResults] = useState<IResult[]>([])

  // La tienda no vive en la sesion, se resuelve por el usuario logueado.
  useEffect(() => {
    webApiService.getStoreByUser(userState.id).then(res => {
      console.log('🚀 ~ getStoreByUser ~ res.data:', res.data)
      if (res.data.length > 0) setStore(res.data[0])
    }).catch((err) => {
      console.log('🚀 ~ getStoreByUser ~ err:', err)
    })
  }, [userState.id])

  // Se muestra "id - nombre" con el primer campo de texto que traiga el servicio.
  const storeLabel = store !== null
    ? [store.id, store.name ?? store.store ?? store.description ?? store.nombre ?? store.code]
        .filter(value => value !== null && value !== undefined && String(value).trim() !== '')
        .join(' - ')
    : ''

  const reset = (): void => {
    setFileName(null)
    setHeaders([])
    setRows([])
    setSkipped(0)
    setResults([])
    setUploading(false)
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file === undefined) return

    setParsing(true)
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target?.result, { type: 'binary' })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        const sheetRows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false })

        const parsedHeaders = (sheetRows[0] ?? [])
          .map((header: any) => String(header ?? '').trim())
          .filter((header: string) => header !== '')

        // Índice de cada columna esperada dentro del excel.
        const indexOf = (column: string): number =>
          parsedHeaders.findIndex((header: string) => normalize(header) === normalize(column))
        const brandIndex = indexOf('Marca')
        const modelIndex = indexOf('Modelo')
        const capacityIndex = indexOf('Capacidad')

        const cell = (row: any[], index: number): string =>
          index === -1 ? '' : String(row[index] ?? '').trim()

        const parsedRows: IRow[] = []
        let invalid = 0
        for (const row of sheetRows.slice(1)) {
          const brand = cell(row, brandIndex)
          const model = cell(row, modelIndex)
          const capacity = cell(row, capacityIndex)
          // Marca y modelo son obligatorios, la capacidad es opcional.
          if (brand === '' || model === '') {
            invalid++
            continue
          }
          parsedRows.push({ brand, model, capacity: capacity === '' ? null : capacity })
        }

        setFileName(file.name)
        setHeaders(parsedHeaders)
        setRows(parsedRows)
        setSkipped(invalid)
        setResults([])
      } catch (error) {
        console.log('🚀 ~ onDrop ~ error:', error)
        reset()
      } finally {
        setParsing(false)
      }
    }
    reader.readAsBinaryString(file)
  }, [])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({ onDrop, accept: ACCEPT, noClick: true, noKeyboard: true })

  // Columnas obligatorias que no vinieron en el archivo.
  const missingColumns = fileName === null
    ? []
    : EXPECTED_COLUMNS
      .filter(column => column.required)
      .filter(column => !headers.some(header => normalize(header) === normalize(column.name)))
      .map(column => column.name)
  const isValidFile = fileName !== null && missingColumns.length === 0 && rows.length > 0

  const descargarPlantilla = (): void => {
    const worksheet = XLSX.utils.aoa_to_sheet([EXPECTED_COLUMNS.map(column => column.name)])
    worksheet['!cols'] = [{ wch: 20 }, { wch: 30 }, { wch: 15 }]
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cotizaciones')
    XLSX.writeFile(workbook, 'plantilla_carga_masiva_telcel.xlsx')
  }

  const cargarCotizaciones = async (): Promise<void> => {
    setUploading(true)
    const acumulado: IResult[] = []

    for (const item of rows) {
      const objCotizacion = {
        brand: item.brand,
        model: item.model,
        capacity: item.capacity,
        seller: slug,
        user: {
          id: userState.id,
          name: userState.name,
          email: userState.email
        },
        store
      }

      await webApiService.saveFromBulkTelcel(objCotizacion).then((res) => {
        acumulado.push({ ok: true, message: String(res?.message ?? res?.data ?? 'Cargada') })
      }).catch((err) => {
        console.log('🚀 ~ saveFromBulkTelcel ~ err:', err)
        acumulado.push({ ok: false, message: String(err?.response?.data?.message ?? 'Error al cargar el registro') })
      })
      setResults([...acumulado])
    }

    const conError = acumulado.filter(result => !result.ok).length
    setUploading(false)
    console.log(`🚀 ~ cargarCotizaciones ~ ${acumulado.length - conError} cargadas, ${conError} con error`)
  }

  const uploaded = results.length
  const failed = results.filter(result => !result.ok).length
  const isDone = !uploading && uploaded > 0 && uploaded === rows.length

  return (
    <div className='bg-white rounded-lg shadow-lg p-6'>
      <div className='border-b-1 pb-3 mb-4'>
        <h6 className='font-bold text-xl'>Carga Masiva de Cotizaciones</h6>
        <span className='text-sm font-normal text-slate-400'>
          Sube el Excel con el formato requerido. Cada cotización se registra con el usuario y la tienda de la sesión.
        </span>
      </div>

      {/* Referencia de columnas esperadas */}
      <div className='rounded-xl bg-slate-50 p-3 mb-3'>
        <div className='flex items-center justify-between gap-2 mb-2'>
          <p className='text-xs font-semibold text-slate-500'>Columnas requeridas (en este orden):</p>
          <Button
            size='sm'
            variant='flat'
            color='primary'
            startContent={<MdFileDownload className='text-base' />}
            onPress={descargarPlantilla}
          >
            Descargar Plantilla
          </Button>
        </div>
        <div className='flex flex-wrap gap-1.5'>
          {EXPECTED_COLUMNS.map(column => (
            <Chip key={column.name} size='sm' variant='flat' color={column.required ? 'primary' : 'default'}>
              {column.name}{!column.required && ' (opcional)'}
            </Chip>
          ))}
        </div>
      </div>

      {/* Datos con los que se registra la carga */}
      <div className='rounded-xl bg-slate-50 p-3 mb-3'>
        <p className='text-xs font-semibold text-slate-500 mb-2'>Datos de la sesión:</p>
        <div className='flex flex-wrap gap-x-6 gap-y-1 text-sm'>
          <span>Usuario: <strong>{userState.name}</strong> <span className='text-slate-400'>({userState.email})</span></span>
          <span>
            Tienda: {store !== null
            ? <strong>{storeLabel}</strong>
            : <span className='text-danger'>sin tienda asociada al usuario</span>}
          </span>
        </div>
      </div>

      {/* Zona de carga del archivo */}
      <div {...getRootProps()}>
        <input {...getInputProps()} />
        {fileName === null
          ? (
            <button
              type='button'
              onClick={open}
              className={`flex flex-col items-center justify-center gap-2 w-full rounded-xl border-2 border-dashed py-10 transition-colors ${isDragActive ? 'border-primary-400 bg-primary-50/50' : 'border-slate-300 hover:border-primary-400 hover:bg-primary-50/50'}`}
            >
              <MdUploadFile className='text-4xl text-primary-500' />
              <span className='text-sm font-medium text-slate-600'>
                {parsing ? 'Leyendo archivo...' : isDragActive ? 'Suelta el archivo aquí' : 'Arrastra el Excel o haz clic para seleccionarlo'}
              </span>
              <span className='text-xs text-slate-400'>Formato: .xlsx</span>
            </button>
            )
          : (
            <>
              <div className='flex items-center justify-between gap-2 rounded-xl border-1 border-slate-200 px-4 py-3'>
                <div className='flex items-center gap-2 min-w-0'>
                  <MdInsertDriveFile className='text-2xl text-emerald-500 shrink-0' />
                  <div className='min-w-0'>
                    <p className='text-sm font-medium truncate'>{fileName}</p>
                    <p className='text-xs text-slate-400'>
                      {headers.length} columnas · {rows.length} cotizaciones{skipped > 0 && ` · ${skipped} filas omitidas`}
                    </p>
                  </div>
                </div>
                <div className='flex items-center gap-1 shrink-0'>
                  <Button
                    size='sm'
                    variant='flat'
                    isDisabled={uploading}
                    startContent={<MdUploadFile className='text-base' />}
                    onPress={open}
                  >
                    Cambiar archivo
                  </Button>
                  <Button isIconOnly size='sm' variant='light' aria-label='Quitar archivo' isDisabled={uploading} onPress={reset}>
                    <MdClose />
                  </Button>
                </div>
              </div>

              {/* Resultado de la validación del archivo */}
              <div className='mt-3'>
                {isValidFile
                  ? (
                    <div className='flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-emerald-700'>
                      <MdCheckCircle className='text-xl shrink-0' />
                      <span className='text-sm font-medium'>El archivo cumple con el formato requerido.</span>
                    </div>
                    )
                  : (
                    <div className='flex flex-col gap-1 rounded-xl bg-red-50 px-4 py-3 text-red-700'>
                      <div className='flex items-center gap-2'>
                        <MdError className='text-xl shrink-0' />
                        <span className='text-sm font-semibold'>El archivo no cumple con el formato:</span>
                      </div>
                      <ul className='list-disc pl-9 text-sm'>
                        {missingColumns.length > 0 && <li>Faltan columnas obligatorias: {missingColumns.join(', ')}.</li>}
                        {missingColumns.length === 0 && rows.length === 0 && <li>No hay filas con marca y modelo para cargar.</li>}
                      </ul>
                    </div>
                    )}
                {skipped > 0 && (
                  <p className='text-xs text-slate-400 mt-2'>
                    Se omitieron {skipped} fila(s) del excel por no tener marca o modelo.
                  </p>
                )}
              </div>
            </>
            )}
      </div>

      {/* Vista previa de lo que se va a cargar */}
      {rows.length > 0 && (
        <div className='mt-4 flex flex-col gap-2'>
          <div className='flex flex-wrap items-center gap-2'>
            <h4 className='text-sm font-semibold text-slate-700'>Vista previa</h4>
            <Chip variant='flat' size='sm'>{rows.length} en total</Chip>
            {uploaded > 0 && <Chip color='success' variant='flat' size='sm'>{uploaded - failed} cargadas</Chip>}
            {failed > 0 && <Chip color='danger' variant='flat' size='sm'>{failed} con error</Chip>}
          </div>
          <Table aria-label='Vista previa de las cotizaciones a cargar' isHeaderSticky classNames={{ wrapper: 'max-h-[420px] shadow-none border-1 border-slate-200' }}>
            <TableHeader>
              <TableColumn key='row' align='center'>Fila</TableColumn>
              <TableColumn key='brand'>Marca</TableColumn>
              <TableColumn key='model'>Modelo</TableColumn>
              <TableColumn key='capacity'>Capacidad</TableColumn>
              <TableColumn key='status' align='center'>Estado</TableColumn>
            </TableHeader>
            <TableBody>
              {rows.map((item, index) => {
                // Los resultados se van agregando en el mismo orden de la lista.
                const result = results[index]
                return (
                  <TableRow key={index}>
                    {/* La fila 1 del excel son los encabezados */}
                    <TableCell className='text-slate-400'>{index + 2}</TableCell>
                    <TableCell>{item.brand}</TableCell>
                    <TableCell>{item.model}</TableCell>
                    <TableCell>{item.capacity ?? <span className='text-slate-300'>—</span>}</TableCell>
                    <TableCell>
                      {result !== undefined
                        ? <Chip size='sm' variant='flat' color={result.ok ? 'success' : 'danger'}>{result.message}</Chip>
                        : uploading
                          ? <Spinner size='sm' />
                          : <Chip size='sm' variant='flat'>Pendiente</Chip>}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Progreso de la carga */}
      {uploading && (
        <div className='mt-3 flex flex-col gap-1'>
          <div className='flex items-center justify-between text-xs font-medium text-slate-500'>
            <span>Cargando cotizaciones…</span>
            <span>{uploaded} / {rows.length}</span>
          </div>
          <Progress aria-label='Progreso de la carga' size='sm' color='primary' value={uploaded} maxValue={rows.length || 1} />
        </div>
      )}

      {isDone && (
        <div className={`mt-3 flex items-center gap-2 rounded-xl px-4 py-3 ${failed > 0 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
          {failed > 0 ? <MdError className='text-xl shrink-0' /> : <MdCheckCircle className='text-xl shrink-0' />}
          <span className='text-sm font-medium'>
            Carga finalizada: {uploaded - failed} cotización(es) cargada(s){failed > 0 && `, ${failed} con error`}.
          </span>
        </div>
      )}

      <div className='flex justify-end gap-2 mt-4'>
        <Button variant='light' isDisabled={uploading || fileName === null} onPress={reset}>
          Cancelar
        </Button>
        <Button
          color={isDone && failed === 0 ? 'success' : 'primary'}
          className={isDone && failed === 0 ? 'text-white' : ''}
          isLoading={uploading}
          isDisabled={!isValidFile || uploading || isDone}
          onPress={() => { cargarCotizaciones().catch((err) => console.log('🚀 ~ cargarCotizaciones ~ err:', err)) }}
        >
          {uploading ? 'Cargando…' : isDone ? 'Carga finalizada' : 'Comenzar importación'}
        </Button>
      </div>
    </div>
  )
}

export default BulkTelcelPage
